import { css } from 'aphrodite';
import * as React from 'react';
import { IPostPersonalApiKeyInput } from '../../../../queries/PersonalApiKeys/usePostPersonalApiKey';
import { Button } from '../../../components-v2/Button';
import { Checkbox } from '../../../components-v2/Checkbox';
import {
	Dialog,
	DialogBody,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../components-v2/Dialog';
import { Field, FieldError, FieldLabel, FieldGroup } from '../../../components-v2/Field';
import { Input } from '../../../components-v2/Input';
import { Label } from '../../../components-v2/Label';
import { Pill } from '../../../components-v2/Pill';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components-v2/Select';
import {
	COMING_SOON_SCOPES,
	DEFAULT_EXPIRATION_DAYS,
	EXPIRATION_DAY_OPTIONS,
	PUBLIC_API_SCOPE_VALUES,
} from './constants';
import { styleSheet } from './styles';

interface IProps {
	isOpen: boolean;
	onOpenChange: (open: boolean) => void;
	onGenerate: (input: IPostPersonalApiKeyInput) => void;
}

export const GeneratePersonalApiKeyDialog: React.FC<IProps> = ({ isOpen, onOpenChange, onGenerate }) => {
	const [description, setDescription] = React.useState('');
	const [descriptionTouched, setDescriptionTouched] = React.useState(false);
	const [scopes, setScopes] = React.useState<string[]>([]);
	const [expirationDays, setExpirationDays] = React.useState<number>(DEFAULT_EXPIRATION_DAYS);

	React.useEffect(() => {
		if (isOpen) {
			setDescription('');
			setDescriptionTouched(false);
			setScopes([]);
			setExpirationDays(DEFAULT_EXPIRATION_DAYS);
		}
	}, [isOpen]);

	const hasDescription = description.trim().length > 0;
	const canGenerate = hasDescription && scopes.length > 0 && !!expirationDays;

	const toggleScope = (scope: string, checked: boolean) => {
		setScopes(current => (checked ? [...current, scope] : current.filter(s => s !== scope)));
	};

	const generate = () => {
		onGenerate({ description: description.trim(), scopes, expirationDays });
	};

	return (
		<Dialog open={isOpen} onOpenChange={onOpenChange}>
			<DialogPopup>
				<DialogHeader>
					<DialogTitle>Create Personal API Key</DialogTitle>
				</DialogHeader>
				<DialogBody>
					<FieldGroup styles={[styleSheet.dialogForm]}>
						<Field data-invalid={descriptionTouched && !hasDescription}>
							<FieldLabel htmlFor='personal-api-key-description'>Description</FieldLabel>
							<Input
								id='personal-api-key-description'
								placeholder='Description'
								type='text'
								value={description}
								aria-invalid={descriptionTouched && !hasDescription}
								onChange={e => setDescription(e.target.value)}
								onBlur={() => setDescriptionTouched(true)}
							/>
							{descriptionTouched && !hasDescription ? <FieldError>Description is required</FieldError> : null}
						</Field>

						<Field>
							<FieldLabel>Scopes</FieldLabel>
							<div className={css(styleSheet.scopeList)}>
								{PUBLIC_API_SCOPE_VALUES.map(scope => {
									const comingSoon = COMING_SOON_SCOPES.has(scope);
									return (
										<Label key={scope} styles={[comingSoon ? styleSheet.scopeDisabled : styleSheet.scopeOption]}>
											<Checkbox
												disabled={comingSoon}
												checked={!comingSoon && scopes.includes(scope)}
												onCheckedChange={checked => toggleScope(scope, checked === true)}
											/>
											{scope}
											{comingSoon ? <Pill variant='label' color='gray' label='Coming Soon' /> : null}
										</Label>
									);
								})}
							</div>
						</Field>

						<Field>
							<FieldLabel>Expiration</FieldLabel>
							<Select value={String(expirationDays)} onValueChange={value => setExpirationDays(Number(value))}>
								<SelectTrigger>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{EXPIRATION_DAY_OPTIONS.map(days => (
										<SelectItem key={days} value={String(days)}>
											{`${days} days`}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</Field>
					</FieldGroup>
				</DialogBody>
				<DialogFooter>
					<Button variant='outline' onClick={() => onOpenChange(false)}>
						Cancel
					</Button>
					<Button variant='primary' disabled={!canGenerate} onClick={generate}>
						Create
					</Button>
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
};
